import { signal } from '@angular/core';
import { FieldTree, SchemaPathTree } from '@angular/forms/signals';
import { CableCommentsDto, CableDto } from '../../core/mceapi.client.generated';
import { apiOpt, appOpt } from '../../core/mceapi.client.helpers';
import { Option } from '../../shared/functions/functional';
import { buildCableDescriptionSchema, CableDescription, createCableDescription, createIdentificationDto } from './description/cable-description.model';
import { buildEarthingSettingsSchema, createEarthingSettings, createEarthingSettingsDto, EarthingSettings } from './earthing-settings/earthing-settings.component';
import { buildEarthingSchema, createEarthing, createEarthingDto, Earthing } from './earthing/earthing.model';
import { buildEarthFaultLoopSchema, createEarthFaultLoop, createEarthFaultLoopDto, EarthFaultLoop } from './efl/cable-efl.model';
import { createExternalCPC, createExternalCPCDto, ExternalCPC } from './external-cpc/external-cpc.component';
import { buildFaultSourcesSchema, createFaultSource, createFaultSourceDto, FaultSource } from './fault-source/fault-source.model';
import { buildInstallationSchema, createInstallation, createInstallationDto, Installation } from './installation/cable-installation.model';
import { buildLoadSchema, createLoad, createLoadDto, Load } from './load/load.model';
import { buildProtectionSchema, createProtection, createProtectionDto, Protection } from './protection/cable-protection.component';
import { buildRCDSchema, createRCD, createRCDDto, RCD } from './protection/rcd/rcd.component';
import { buildCableSelectionSchema, createCableSelection, createCableSelectionDto, SelectedCable } from './selection/cable-selection.model';
import { buildCableSettingsSchema, CableSettings, createCableSettings, createCableSettingsDto } from './settings/cable-settings';
import { buildSystemVoltageSchema, createSystemVoltage, createSystemVoltageDto, SystemVoltage } from './system-voltage/system-voltage.model';
import { buildVoltageDropSchema, createVoltageDrop, createVoltageDropDto, VoltageDrop } from './voltage-drop/cable-voltage-drop.model';
import { buildCableCommentsSchema, CableComments, createCableComments } from './comments/cable-comments.model';


export interface EditCable {
  description: CableDescription;
  systemVoltage: SystemVoltage;
  earthing: Earthing;
  earthingSettings: EarthingSettings;
  externalCPC: ExternalCPC;
  load: Load;
  protection: Protection;
  rcd: RCD;
  selectedCable: SelectedCable;
  installation: Installation;
  voltageDrop: VoltageDrop;
  faultSource: FaultSource;
  earthFaultLoop: EarthFaultLoop;
  settings: CableSettings;
  comments: CableComments;
}

function requireDtoValue<T>(value: T | null | undefined, path: string): T {
  if (value == null) {
    throw new Error(`Cable DTO is missing required field '${path}'.`);
  }

  return value;
}

export function buildEditCableSchema(path: SchemaPathTree<EditCable>) {
  buildCableDescriptionSchema(path.description);
  buildSystemVoltageSchema(path.systemVoltage);
  buildEarthingSchema(path.earthing);
  buildEarthingSettingsSchema(path.earthingSettings);
  buildLoadSchema(path.load);
  buildProtectionSchema(path.protection);
  buildRCDSchema(path.rcd);
  buildCableSelectionSchema(path.selectedCable);
  buildInstallationSchema(path.installation);
  buildVoltageDropSchema(path.voltageDrop);
  buildFaultSourcesSchema(path.faultSource);
  buildEarthFaultLoopSchema(path.earthFaultLoop);
  buildCableSettingsSchema(path.settings);
  buildCableCommentsSchema(path.comments);
}


export function createEditCable(dto: CableDto, dtoComments: Option<CableCommentsDto>) {
  const uid = requireDtoValue(dto.uid, 'uid');
  const identification = requireDtoValue(dto.identification, 'identification');
  const supply = requireDtoValue(dto.supply, 'supply');
  const systemVoltage = requireDtoValue(supply.systemVoltage, 'supply.systemVoltage');
  const cableEarthing = requireDtoValue(supply.earthing, 'supply.earthing');
  const earthing = requireDtoValue(cableEarthing.earthing, 'supply.earthing.earthing');
  const earthingSettings = requireDtoValue(cableEarthing.settings, 'supply.earthing.settings');

  return signal<EditCable>({
    description: createCableDescription(uid, identification)(),
    systemVoltage: createSystemVoltage(systemVoltage)(),
    earthing: createEarthing(earthing)(),
    load: createLoad(requireDtoValue(dto.load, 'load'))(),
    selectedCable: createCableSelection(appOpt(dto.selectedCable ?? {}))(),
    earthingSettings: createEarthingSettings(earthingSettings)(),
    externalCPC: createExternalCPC(appOpt(earthingSettings.externalCPC ?? {}))(),
    protection: createProtection(appOpt(dto.protection?.device ?? {}))(),
    rcd: createRCD(appOpt(dto.protection?.rcd ?? {}))(),
    installation: createInstallation(requireDtoValue(dto.installation, 'installation'))(),
    voltageDrop: createVoltageDrop(requireDtoValue(dto.voltageDrop, 'voltageDrop'))(),
    faultSource: createFaultSource(requireDtoValue(dto.faultSource, 'faultSource'))(),
    earthFaultLoop: createEarthFaultLoop(dto.earthFaultLoop ?? {})(),
    settings: createCableSettings(requireDtoValue(dto.settings, 'settings'))(),
    comments: createCableComments(uid, dtoComments)()
  })

}

export function createEditCableDto(model: FieldTree<EditCable>, base: CableDto) {
  const systemVoltage = requireDtoValue(createSystemVoltageDto(model.systemVoltage), 'systemVoltage');
  const earthing = requireDtoValue(createEarthingDto(model.earthing), 'earthing');
  const load = requireDtoValue(createLoadDto(model.load), 'load');

  return signal<CableDto>({
    ...base, // keep properties that are not edited
    identification: createIdentificationDto(model.description)(),
    supply: {
      systemVoltage: systemVoltage(),
      earthing: {
        earthing: earthing(),
        settings: createEarthingSettingsDto(model.earthingSettings, createExternalCPCDto(model.externalCPC)())()
      }
    },
    load: load(),
    protection: {
      device: apiOpt(createProtectionDto(model.protection)()),
      rcd: apiOpt(createRCDDto(model.rcd)())
    },
    selectedCable: apiOpt(createCableSelectionDto(model.selectedCable)()),
    installation: createInstallationDto(model.installation)(),
    voltageDrop: createVoltageDropDto(model.voltageDrop)(),
    faultSource: createFaultSourceDto(model.faultSource)(),
    earthFaultLoop: apiOpt(createEarthFaultLoopDto(model.earthFaultLoop)()),
    settings: createCableSettingsDto(model.settings)()
  })
}

